import * as Sentry from "@sentry/browser";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { Address, Hash } from "viem";
import { getNetwork } from "wagmi/actions";
import { PERMIT_TWO_CONTRACT_ADDRESS, SENTRY_CONTEXTS } from "../../config/consts";
import { Amount } from "../../types/amount";
import { GlobalStore } from "./GlobalStore";
import { TokenList } from "./getAllTokenLists";
import { getMulticallResult } from "./getMulticall";

export interface Allowance {
  tokenAddress: Hash;
  allowance: Amount;
}

export type Allowances = Array<Allowance>;
const fetchPermit2Allowances = async (
  address: Address,
  tokenList: TokenList | undefined
): Promise<Allowances> => {
  const { chain } = getNetwork();
  if (chain && tokenList) {
    const erc20TokenAllowances = await getMulticallResult(tokenList, chain, "allowance", [
      address,
      PERMIT_TWO_CONTRACT_ADDRESS,
    ]);
    return erc20TokenAllowances.map(res => ({
      tokenAddress: res.tokenAddress,
      allowance: res.result,
    }));
  }
  return Promise.reject("Invalid or no chain");
};

export const getPermit2Allowances = createAsyncAction(
  async ({ address, tokenList }: { address: `0x${string}`; tokenList: TokenList | undefined }) => {
    try {
      Sentry.configureScope(scope => {
        scope.setTransactionName(SENTRY_CONTEXTS.ALLOWANCES);
      });
      const result = await fetchPermit2Allowances(address, tokenList);
      if (result && result.length > 0) {
        return successResult(result);
      }
    } catch (e) {
      Sentry.captureException(`Could not get allowances`, { extra: { event: e } });
      return errorResult([], `Could not get allowances`, e);
    }
    Sentry.captureException(`Could not get allowances`);
    return errorResult([], `Couldn't get allowances`);
  },
  {
    postActionHook: ({ result }) => {
      if (!result.error) {
        GlobalStore.update(s => {
          s.allowances = result.payload;
        });
      }
    },
  }
);
